import { useFormik } from "formik";
import { motion } from "framer-motion";
import { useState } from "react";
import { FaCamera, FaEye, FaEyeSlash, FaUserCircle } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import logo from "../assest/logo.png";
import SummaryApi from "../common";
import {
  APP_SHORT_NAME,
  EMAIL_PLACEHOLDER,
  NAME_PLACEHOLDER,
  PASSWORD_PLACEHOLDER,
  ROUTES,
  SIGNUP_SUBTITLE,
  SIGNUP_TITLE,
} from "../constants";
import imageTobase64 from "../helpers/imageTobase64";
import { request } from "../helpers/request";
import { signUpSchema } from "../helpers/validationSchema";

const STRENGTH_CONFIG = {
  Weak: { color: "text-red-500", bar: "bg-red-400", width: "w-1/3" },
  Moderate: { color: "text-yellow-500", bar: "bg-yellow-400", width: "w-2/3" },
  Strong: { color: "text-green-500", bar: "bg-green-400", width: "w-full" },
};

const SignUp = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("");
  const [profilePic, setProfilePic] = useState("");
  const navigate = useNavigate();

  const getPasswordStrength = (password) => {
    if (!password) return "";
    if (password.length < 6) return "Weak";
    if (
      password.match(/[A-Z]/) &&
      password.match(/[0-9]/) &&
      password.match(/[!@#$%^&*]/)
    )
      return "Strong";
    return "Moderate";
  };

  const handleUploadPic = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const imagePic = await imageTobase64(file);
    setProfilePic(imagePic);
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      ageVerified: false,
    },
    validationSchema: signUpSchema,
    onSubmit: async (values) => {
      try {
        const dataResponse = await request(SummaryApi.signUP, {
          body: JSON.stringify({ ...values, profilePic }),
        });
        const dataApi = await dataResponse.json();
        if (dataApi.success) {
          toast.success(dataApi.message);
          navigate(ROUTES.LOGIN);
        } else {
          toast.error(dataApi.message);
        }
      } catch {
        toast.error("An error occurred. Please try again.");
      }
    },
  });

  const handlePasswordChange = (e) => {
    formik.handleChange(e);
    setPasswordStrength(getPasswordStrength(e.target.value));
  };

  const strengthCfg = STRENGTH_CONFIG[passwordStrength];

  return (
    <section className="min-h-[calc(100vh-4rem)] flex items-start md:items-center justify-center bg-surface-muted px-4 py-10 md:py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-lg bg-white rounded-2xl border border-surface-border shadow-card p-6 md:p-10"
      >
        {/* Mobile logo */}
        <Link to={ROUTES.HOME} className="flex items-center gap-2 mb-6">
          <img
            src={logo}
            alt={APP_SHORT_NAME}
            className="w-7 h-7 object-contain"
          />
          <span className="font-black text-lg text-primary">
            {APP_SHORT_NAME}
          </span>
        </Link>

        {/* Avatar picker */}
        <div className="flex items-center gap-5 mb-8">
          <div className="relative flex-shrink-0">
            <div className="w-16 h-16 rounded-2xl bg-surface-muted border-2 border-surface-border overflow-hidden">
              {profilePic ? (
                <img
                  src={profilePic}
                  className="w-full h-full object-cover"
                  alt="Profile"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-neutral-300">
                  <FaUserCircle className="text-4xl" />
                </div>
              )}
            </div>
            <label className="absolute -bottom-1.5 -right-1.5 w-7 h-7 bg-primary text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-accent transition-all shadow-md">
              <FaCamera size={10} />
              <input
                type="file"
                className="hidden"
                onChange={handleUploadPic}
                accept="image/*"
              />
            </label>
          </div>
          <div>
            <h1 className="text-2xl font-black text-primary tracking-tight">
              {SIGNUP_TITLE}
            </h1>
            <p className="text-neutral-500 text-sm mt-0.5">{SIGNUP_SUBTITLE}</p>
          </div>
        </div>

        <form onSubmit={formik.handleSubmit} className="space-y-4" noValidate>
          {/* Name */}
          <div>
            <label className="block text-2xs font-black uppercase tracking-widest text-neutral-400 mb-1.5 ml-0.5">
              Full Name
            </label>
            <input
              type="text"
              name="name"
              placeholder={NAME_PLACEHOLDER}
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`input-base ${formik.touched.name && formik.errors.name ? "border-red-400 bg-red-50" : ""}`}
            />
            {formik.touched.name && formik.errors.name && (
              <p className="text-red-500 text-2xs font-semibold mt-1 ml-0.5">
                {formik.errors.name}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-2xs font-black uppercase tracking-widest text-neutral-400 mb-1.5 ml-0.5">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              placeholder={EMAIL_PLACEHOLDER}
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`input-base ${formik.touched.email && formik.errors.email ? "border-red-400 bg-red-50" : ""}`}
            />
            {formik.touched.email && formik.errors.email && (
              <p className="text-red-500 text-2xs font-semibold mt-1 ml-0.5">
                {formik.errors.email}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-2xs font-black uppercase tracking-widest text-neutral-400 mb-1.5 ml-0.5">
              Password
            </label>
            <div
              className={`relative input-base flex items-center h-12 pr-0 ${formik.touched.password && formik.errors.password ? "border-red-400 bg-red-50" : ""}`}
            >
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder={PASSWORD_PLACEHOLDER}
                value={formik.values.password}
                onChange={handlePasswordChange}
                onBlur={formik.handleBlur}
                className="flex-1 h-full outline-none bg-transparent text-sm font-medium text-primary placeholder:text-neutral-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                className="px-3 h-full text-neutral-400 hover:text-primary transition-colors"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {/* Strength bar */}
            {formik.values.password &&
              !formik.errors.password &&
              strengthCfg && (
                <div className="flex items-center gap-2 mt-1.5 ml-0.5">
                  <div className="flex-1 h-1 bg-neutral-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${strengthCfg.bar} ${strengthCfg.width}`}
                    />
                  </div>
                  <span className={`text-2xs font-bold ${strengthCfg.color}`}>
                    {passwordStrength}
                  </span>
                </div>
              )}
            {formik.touched.password && formik.errors.password && (
              <p className="text-red-500 text-2xs font-semibold mt-1 ml-0.5">
                {formik.errors.password}
              </p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-2xs font-black uppercase tracking-widest text-neutral-400 mb-1.5 ml-0.5">
              Confirm Password
            </label>
            <div
              className={`relative input-base flex items-center h-12 pr-0 ${formik.touched.confirmPassword && formik.errors.confirmPassword ? "border-red-400 bg-red-50" : ""}`}
            >
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                placeholder={PASSWORD_PLACEHOLDER}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="flex-1 h-full outline-none bg-transparent text-sm font-medium text-primary placeholder:text-neutral-400"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((p) => !p)}
                className="px-3 h-full text-neutral-400 hover:text-primary transition-colors"
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {formik.touched.confirmPassword &&
              formik.errors.confirmPassword && (
                <p className="text-red-500 text-2xs font-semibold mt-1 ml-0.5">
                  {formik.errors.confirmPassword}
                </p>
              )}
          </div>

          {/* Age verification */}
          <div className="flex items-start gap-3 p-3 bg-surface-muted rounded-xl border border-surface-border">
            <input
              id="ageVerified"
              name="ageVerified"
              type="checkbox"
              checked={formik.values.ageVerified}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="mt-0.5 w-4 h-4 rounded border-neutral-300 text-accent accent-accent cursor-pointer flex-shrink-0"
            />
            <div>
              <label
                htmlFor="ageVerified"
                className="text-sm font-semibold text-primary cursor-pointer"
              >
                I am at least 13 years old{" "}
                <span className="text-red-500">*</span>
              </label>
              <p className="text-2xs text-neutral-400 mt-0.5">
                Required for COPPA compliance.
              </p>
              {formik.touched.ageVerified && formik.errors.ageVerified && (
                <p className="text-red-500 text-2xs font-semibold mt-0.5">
                  {formik.errors.ageVerified}
                </p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={formik.isSubmitting}
            className="w-full h-12 bg-primary text-white font-bold text-sm rounded-xl hover:bg-accent transition-all duration-300 shadow-button-primary active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {formik.isSubmitting ? "Creating Account..." : "Create Account"}
          </button>
        </form>

        <p className="mt-6 text-center text-neutral-500 text-sm">
          Already have an account?{" "}
          <Link
            to={ROUTES.LOGIN}
            className="text-accent font-bold hover:underline"
          >
            Sign in
          </Link>
        </p>
      </motion.div>
    </section>
  );
};

export default SignUp;
